import React from "react"
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/styles";
import FormControlLabel from '@mui/material/FormControlLabel';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import TeamRepositoryDropdown from "../../../../../components/DropDowns/TeamRepositoryDropdown";
import RepositoryTagsMultiSelect from "../../../../../components/MultiSelect/RepositoryTagsMultiSelect";

const RepositoryControls = ({ matchTypeSelection, handleMatchTypeSelection, tagTypesForDropdown, customTagCombination, selectedTableDetail, handleSelectedTableDetails, userFilter, handleUserChange, displaySelection, handleDisplayChange, handleOpenUploadModal }) => {
  const theme = useTheme()

  const displayControlProps = (item) => ({
    checked: displaySelection === item,
    onChange: handleDisplayChange,
    value: item,
    label: item,
    labelPlacement: "end",
    sx: {
      color: `${theme.palette.common.grayDark}`,
      padding: "0",
      margin: "0",
    }
  })
  const matchTypeSelectionProp = (item) => ({
    checked: matchTypeSelection === item,
    onChange: handleMatchTypeSelection,
    value: item,
    label: item,
    labelPlacement: "end",
    sx: {
      color: `${theme.palette.common.grayDark}`,
      padding: "0",
      margin: "0",
    }
  })

  const radioControlProps = (disabled = false) => ({
    disabled,
    sx: {
      padding: "0.18rem",
      color: disabled ? theme.palette.common.grayBorderDark : theme.palette.common.grayBorderDarkest,
      "&.Mui-checked": {
        color: theme.palette.primary.dark,
      },
      "&:hover": {
        backgroundColor: theme.palette.primary.labelTransparent,
      },
      "& .MuiSvgIcon-root": {
        width: "1rem",
        height: "1rem",
      },
    },
  })

  return (
    <ControlsRow>
      <ControlGroup>
        <ControlLabel>Display</ControlLabel>
        <FormControl>
          <StyledRadioGroup row>
            <FormControlLabel
              control={<Radio {...radioControlProps()} />}
              {...displayControlProps('Table View')} 
            />
            <FormControlLabel
              control={<Radio {...radioControlProps()} />}
              {...displayControlProps('Chart View')} 
            />
          </StyledRadioGroup>
        </FormControl>
      </ControlGroup>

      <VerticalRule />

      {displaySelection === 'Table View' ?
        <FiltersGroup>
          <ControlLabel>Filter by</ControlLabel>

          <StackedFilterGroup>
            <FieldLabel>Mortgage Detail</FieldLabel>
            <InlineFilterRow>
              <InlineLabel $muted={customTagCombination.length === 0}>match</InlineLabel>
              <FormControl>
                <StyledRadioGroup row>
                  <FormControlLabel
                    control={<Radio {...radioControlProps(customTagCombination.length === 0)} />}
                    {...matchTypeSelectionProp('all')} 
                  />
                  <FormControlLabel
                    control={<Radio {...radioControlProps(customTagCombination.length === 0)} />}
                    {...matchTypeSelectionProp('any')} 
                  />
                  <FormControlLabel
                    control={<Radio {...radioControlProps(customTagCombination.length === 0)} />}
                    {...matchTypeSelectionProp('none')} 
                  />
                </StyledRadioGroup>
              </FormControl>
            </InlineFilterRow>
          </StackedFilterGroup>

          <DropdownSlot className="repositoryTagSelect">
            <RepositoryTagsMultiSelect
              value={selectedTableDetail}
              handleChange={handleSelectedTableDetails}
              items={tagTypesForDropdown}
              origin={'table'}
            />
          </DropdownSlot>

          <AssigneeGroup>
            <FieldLabel>Investigation Assignee</FieldLabel>
            <DropdownSlot className="repositoryAssigneeSelect">
              <TeamRepositoryDropdown userFilter={userFilter} handleUserChange={handleUserChange} />
            </DropdownSlot>
          </AssigneeGroup>
        </FiltersGroup>
      : displaySelection === 'Chart View' &&
        <FiltersGroup>
          <StackedFilterGroup>
            <FieldLabel>Custom Detail Combination</FieldLabel>
            <InlineFilterRow>
              <InlineLabel $muted={customTagCombination.length === 0}>match</InlineLabel>
              <FormControl>
                <StyledRadioGroup row>
                  <FormControlLabel
                    control={<Radio {...radioControlProps(customTagCombination.length === 0)} />}
                    {...matchTypeSelectionProp('all')} 
                  />
                  <FormControlLabel
                    control={<Radio {...radioControlProps(customTagCombination.length === 0)} />}
                    {...matchTypeSelectionProp('any')} 
                  />
                  <FormControlLabel
                    control={<Radio {...radioControlProps(customTagCombination.length === 0)} />}
                    {...matchTypeSelectionProp('none')} 
                  />
                </StyledRadioGroup>
              </FormControl>
            </InlineFilterRow>
          </StackedFilterGroup>

          <DropdownSlot className="repositoryTagSelect chart">
            <RepositoryTagsMultiSelect
              value={customTagCombination}
              handleChange={handleSelectedTableDetails}
              items={tagTypesForDropdown}
              origin={'chart'}
            />
          </DropdownSlot>
        </FiltersGroup>
      }

      <ActionGroup>
        <AddMortgageButton type="button" onClick={() => handleOpenUploadModal()}>
          <AddCircleOutlineIcon />
          Add new mortgages
        </AddMortgageButton>
      </ActionGroup>
    </ControlsRow>
  )
}

const ControlsRow = styled("div")(({ theme }) => ({
  width: "100%",
  minHeight: "4.8rem",
  display: "flex",
  alignItems: "center",
  gap: "1rem",
  padding: "0.75rem 1rem",
  background: theme.palette.common.white,
  overflowX: "auto",
  overflowY: "hidden",
  scrollbarWidth: "thin",
  scrollbarColor: `${theme.palette.common.grayBorderDark} ${theme.palette.common.white}`,
  "&::-webkit-scrollbar": {
    height: "0.4rem",
  },
  "&::-webkit-scrollbar-track": {
    background: theme.palette.common.white,
    border: `0.1rem solid ${theme.palette.common.grayBorder}`,
  },
  "&::-webkit-scrollbar-thumb": {
    background: theme.palette.common.grayBorderDark,
    borderRadius: "999px",
  },
}))

const ControlGroup = styled("div")({
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  gap: "0.65rem",
  minWidth: 0,
  height: '3rem',
})

const FiltersGroup = styled("div")({
  flex: "1 1 auto",
  minWidth: "42rem",
  display: "flex",
  alignItems: "center",
  gap: "0.9rem",
  height: '3rem',
})

const StackedFilterGroup = styled("div")({
  flexDirection: 'column',
  display: "flex",
  alignContent: "center",
  gap: "0.18rem",
  minWidth: 0,
  height: '3rem',
})

const InlineFilterRow = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "0.35rem",
  minHeight: "1.55rem",
})

const AssigneeGroup = styled("div")({
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  gap: "0.55rem",
  minWidth: 0,
  marginLeft: '1rem',
})

const ActionGroup = styled("div")({
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  alignSelf: "stretch",
  marginLeft: "auto",
})

const ControlLabel = styled("div")(({ theme }) => ({
  color: theme.palette.common.black,
  fontSize: "0.78rem",
  fontWeight: 900,
  letterSpacing: 0,
  lineHeight: 1,
  whiteSpace: "nowrap",
  marginBottom: 'auto',
}))

const FieldLabel = styled("div")(({ theme }) => ({
  color: theme.palette.common.grayDark,
  fontSize: "0.68rem",
  fontWeight: 900,
  letterSpacing: 0,
  lineHeight: 1,
  textTransform: "uppercase",
  whiteSpace: "nowrap",
  marginBottom: 'auto',
}))

const InlineLabel = styled("span")(({ theme, $muted }) => ({
  color: $muted ? theme.palette.common.grayText : theme.palette.common.grayDark,
  fontSize: "0.72rem",
  fontWeight: 800,
  letterSpacing: 0,
  lineHeight: 1,
}))

const VerticalRule = styled("div")(({ theme }) => ({
  flex: "0 0 auto",
  width: "0.1rem",
  height: "2.4rem",
  background: theme.palette.common.grayBorder,
}))

const StyledRadioGroup = styled(RadioGroup)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "0.3rem",
  flexWrap: "nowrap",
  "& .MuiFormControlLabel-root": {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.25rem",
    margin: 0,
    padding: "0.01rem 0.45rem 0.01rem 0.22rem",
    border: `0.1rem solid ${theme.palette.common.grayBorder}`,
    borderRadius: "999px",
    background: theme.palette.common.white,
    color: theme.palette.common.grayDark,
    transition: "background 140ms ease, border-color 140ms ease, box-shadow 140ms ease",
  },
  "& .MuiFormControlLabel-root:has(.Mui-checked)": {
    borderColor: theme.palette.primary.pastelMedium,
    background: theme.palette.primary.labelTransparent,
    color: theme.palette.primary.dark,
  },
  "& .MuiFormControlLabel-root:hover": {
    borderColor: theme.palette.primary.pastelMedium,
    boxShadow: "0 0.25rem 0.6rem rgba(28, 28, 31, 0.06)",
  },
  "& .MuiFormControlLabel-root:has(.Mui-disabled)": {
    background: theme.palette.common.grayTransparent,
    color: theme.palette.common.grayTextLight,
    boxShadow: "none",
  },
  "& .MuiTypography-root": {
    color: "inherit",
    fontSize: "0.72rem",
    fontWeight: 850,
    letterSpacing: 0,
    lineHeight: 1,
    whiteSpace: "nowrap",
  },
}))

const DropdownSlot = styled("div")(({ theme }) => ({
  flex: "0 1 22rem",
  minWidth: "14rem",
  maxWidth: "28rem",
  marginTop: '1rem',
  display: "flex",
  alignItems: "center",
  "&.chart": {
    flexBasis: "32rem",
    maxWidth: "40rem",
  },
  "& .MuiFormControl-root": {
    width: "100% !important",
    minWidth: "0 !important",
    maxWidth: "100% !important",
    padding: "0 !important",
  },
  "& .MuiInputBase-root": {
    width: "100% !important",
    minHeight: "2.35rem",
    height: "2.35rem !important",
    borderRadius: "0.5rem !important",
    border: `0.1rem solid ${theme.palette.common.grayBorderMedium} !important`,
    background: `${theme.palette.common.white} !important`,
    color: `${theme.palette.common.blackLight} !important`,
    boxShadow: "0 0.25rem 0.55rem rgba(28, 28, 31, 0.035)",
    transition: "border-color 140ms ease, box-shadow 140ms ease, background 140ms ease",
  },
  "& .MuiInputBase-root:hover": {
    borderColor: `${theme.palette.primary.pastelMedium} !important`,
    boxShadow: "0 0.35rem 0.75rem rgba(28, 28, 31, 0.06)",
  },
  "& .Mui-focused": {
    borderColor: `${theme.palette.primary.dark} !important`,
    boxShadow: `0 0 0 0.18rem ${theme.palette.primary.labelTransparent}`,
  },
  "& .MuiSelect-select.MuiSelect-select": {
    minHeight: "2.35rem !important",
    height: "2.35rem !important",
    display: "flex !important",
    alignItems: "center !important",
    justifyContent: "flex-start !important",
    padding: "0 2rem 0 0.75rem !important",
    color: `${theme.palette.common.blackLight} !important`,
    fontSize: "0.78rem !important",
    fontWeight: "800 !important",
    lineHeight: "1.2 !important",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  "& .MuiSelect-icon": {
    right: "0.45rem",
    color: `${theme.palette.primary.dark} !important`,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none !important",
  },
}))

const AddMortgageButton = styled("button")(({ theme }) => ({
  height: "2.6rem",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.45rem",
  border: `0.1rem solid ${theme.palette.primary.pastelMedium}`,
  borderRadius: "0.55rem",
  background: `linear-gradient(180deg, ${theme.palette.primary.pastelMedium} 0%, ${theme.palette.primary.dark} 100%)`,
  boxShadow: "0 0.55rem 1.05rem rgba(0, 148, 15, 0.14)",
  color: theme.palette.common.white,
  cursor: "pointer",
  font: "inherit",
  fontSize: "0.82rem",
  fontWeight: 900,
  letterSpacing: 0,
  lineHeight: 1,
  padding: "0 0.95rem",
  whiteSpace: "nowrap",
  transition: "transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease, filter 140ms ease",
  "& svg": {
    width: "1.05rem",
    height: "1.05rem",
    color: "inherit",
  },
  "&:hover": {
    boxShadow: "0 0.7rem 1.25rem rgba(0, 148, 15, 0.18)",
    filter: "brightness(1.02)",
  },
  "&:active": {
    transform: "translateY(0.05rem)",
    boxShadow: "0 0.35rem 0.8rem rgba(0, 148, 15, 0.14)",
  },
  "&:focus-visible": {
    outline: `0.18rem solid ${theme.palette.primary.transparentMid}`,
    outlineOffset: "0.15rem",
  },
}))

export default RepositoryControls
